import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { SiteHeader } from '../components/layout/SiteHeader';
import { Footer } from '../components/layout/Footer';
import { useAppliedTheme } from '../hooks/useAppliedTheme';
import { useAppStore } from '../store/useAppStore';
import { loadSettings } from '../utils/settingsLoader';
import { trackEvent } from '../utils/posthogClient';

/** Bildet die Route auf einen stabilen `page`-Namen für das Analytics-Event ab. */
function pageNameForPath(pathname: string): { page: string; questionNumber?: number } {
  if (pathname === '/') return { page: 'start' };
  if (pathname === '/vorbereitung') return { page: 'preparation' };
  if (pathname === '/kontext/angebot') return { page: 'context_angebot' };
  if (pathname === '/kontext/instagram') return { page: 'context_instagram' };
  if (pathname === '/kontext/arbeitsweise') return { page: 'context_arbeitsweise' };
  if (pathname === '/pruefen') return { page: 'review' };
  if (pathname === '/ergebnis') return { page: 'result' };
  if (pathname === '/ueber-den-test') return { page: 'about' };
  const questionMatch = /^\/test\/(\d+)$/.exec(pathname);
  if (questionMatch?.[1]) {
    return { page: 'test_question', questionNumber: Number(questionMatch[1]) };
  }
  return { page: 'not_found' };
}

/** App-Schale: Skip-Link, Kopfbereich, Hauptbereich mit Fokus-Management (A11Y-001/002). */
export function App() {
  useAppliedTheme();
  const location = useLocation();
  const mainRef = useRef<HTMLDivElement>(null);
  const setSettings = useAppStore((state) => state.setSettings);
  const analyticsEnabled = useAppStore((state) => state.settings.featureFlags.analytics.enabled);

  useEffect(() => {
    mainRef.current?.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    loadSettings()
      .then((settings) => {
        if (!cancelled) setSettings(settings);
      })
      .catch((err: unknown) => console.error(err));
    return () => {
      cancelled = true;
    };
  }, [setSettings]);

  useEffect(() => {
    if (!analyticsEnabled) return;
    const { page, questionNumber } = pageNameForPath(location.pathname);
    trackEvent('page_view', {
      page,
      path: location.pathname,
      theme: document.documentElement.dataset.theme ?? 'light',
      ...(questionNumber !== undefined ? { question_number: questionNumber } : {}),
    });
  }, [location.pathname, analyticsEnabled]);

  return (
    <>
      <a href="#main-content" className="skip-link">
        Zum Hauptinhalt springen
      </a>
      <SiteHeader />
      <div id="main-content" ref={mainRef} tabIndex={-1}>
        <Outlet />
      </div>
      <Footer />
    </>
  );
}
